import { useQuery } from '@apollo/client';
import { Box, DialogTitle, MenuItem, Select, SelectChangeEvent, styled } from '@mui/material';
import { Plus } from '@styled-icons/bootstrap';
import { Edit } from '@styled-icons/boxicons-solid';
import CreateEditComponentForm, {
  TFormValues,
} from 'FeatureModules/ComponentChain/Components/Forms/CreateEditComponentForm';
import LanguageSelectorItem from 'Components/Forms/LanguageSelectorItem';
import Loader from 'Components/Forms/Loader';
import {
  StyledDialogContent,
  TopBackground,
} from 'FeatureModules/ProductPage/Components/Create/styles';
import { InlineHelperText } from 'FeatureModules/ProductPage/Components/InlineHelperText/InlineHelperText';
import useConfig from 'hooks/useConfig';
import useDialog from 'hooks/useDialog';
import { InfoTooltip } from 'DesignSystem';
import { StyledDialog } from 'DesignSystem/Overlays/Dialog/DialogDefault/DialogDefault';
import { FormikProps } from 'formik';
import { GET_COMPONENT } from 'graphql/secure/queries';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import React, { FC, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IComponentItem } from 'FeatureModules/ComponentChain/types/component.types';
import { IDefaultDialogProps } from 'types/dialog.types';
import { ELanguage } from 'DesignSystem/types/language.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import useComponentItemActions from 'FeatureModules/ComponentChain/hooks/useComponentItemActions';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import PageSubTitle from 'Components/PageSubTitle';

export interface IAddEditComponentDialogProps extends IDefaultDialogProps {
  title: string;
  /* Pass this if the partner select should already be prefilled
   * Warning: This overrides the partner on the componentItem and disabled the field (so the partner can not be changed)
   */
  partner?: IBasePartnerCompany;

  /**
   * The rest of selectable raw material ids for filtering the component selection
   */
  selectableRawMaterialIds?: string[];

  hidePartner?: boolean;
  componentItem?: Omit<IComponentItem, 'inUse' | 'lastEditedTimestamp'>;
  infoText?: string;
  /*
   * Callback will be called after the creation returned result (success or error)
   * By default the user will be redirected to the component overview
   */
  onComponentCreated?: (component: IComponentItem) => void;
}

const DialogTitleStyled = styled(DialogTitle)(() => ({
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingRight: 40,
  paddingBottom: 0,
}));

const StyledSelect = styled(Select)(() => ({
  '& .MuiSelect-select ': {
    padding: '6px 12px !important',
  },

  '& .MuiButtonBase-root': {
    padding: 0,
    paddingRight: 20,
  },
  '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

const StyledMenuItem = styled(MenuItem)(() => ({
  '& .MuiButtonBase-root': {
    padding: 0,
    paddingRight: 20,
  },
}));

const AddEditComponentDialog: FC<IAddEditComponentDialogProps> = ({
  open,
  title,
  partner,
  hidePartner,
  selectableRawMaterialIds,
  componentItem: componentItemProp,
  infoText,
  onClose,
  onComponentCreated,
}) => {
  const { openDialog } = useDialog();
  const [selectedLanguage, setSelectedLanguage] = useState<ELanguage>(ELanguage.ENGLISH);
  const formRef = useRef<FormikProps<TFormValues>>(null);
  const { closeDialog } = useDialog();
  const navigate = useNavigate();
  const { createComponent, updateComponent } = useComponentItemActions();
  const { productLanguages } = useConfig();
  const { data: componentItemData, loading } = useQuery<{
    component: IComponentItem;
  }>(GET_COMPONENT, {
    variables: { id: componentItemProp?.id, lang: selectedLanguage },
    skip: selectedLanguage === ELanguage.ENGLISH || !componentItemProp?.id,
    fetchPolicy: 'no-cache', // don't store in cache because it should not overwrite the componentOverview in the background
  });

  const componentItem =
    selectedLanguage === ELanguage.ENGLISH ? componentItemProp : componentItemData?.component;

  /*
   * Either create or update the component
   * Create callback will be called after the creation returned result (success or error)
   * By default the user will be redirected to the component overview which can be overwritten by the callback
   */
  const handleSubmit = async (values: TFormValues) => {
    if (componentItem) {
      const variables = {
        id: componentItem.id,
        input: {
          ...omit(values, 'ownedBy'),
          ownedById: hidePartner ? partner?.id : values.ownedBy?.id || null,
        },
        lang: selectedLanguage,
      };
      updateComponent({ variables });
    } else {
      const variables = {
        input: {
          ...omit(values, 'ownedBy'),
          ownedById: hidePartner ? partner?.id : values.ownedBy?.id || null,
        },
        lang: selectedLanguage,
      };
      const { data } = await createComponent({ variables });
      const component = data?.createComponent?.component;
      closeDialog('ADD_EDIT_COMPONENT');
      if (component) {
        onComponentCreated
          ? onComponentCreated(component)
          : navigate(`/components/${component.id}`);
      }
    }
  };

  // If there are unsaved changes, ask the user if they want to leave the page
  const handleClose = () => {
    if (formRef.current && !isEqual(formRef.current.values, formRef.current.initialValues)) {
      openDialog({
        type: 'ALERT',
        props: {
          title: 'Unsaved changes',
          text: 'Are you sure you want to close this window? All unsaved changes will be lost and you will not be able to undo this action.',
          submitText: 'Discard changes',
          displayCloseButton: true,
          onSubmit: onClose,
          onCancel: () => undefined,
        },
      });
    } else {
      onClose?.();
    }
  };

  const handleLanguageChange = (event: SelectChangeEvent<unknown>) => {
    setSelectedLanguage(event.target.value as ELanguage);
  };

  return (
    <StyledDialog
      open={!!open}
      onClose={handleClose}
      maxWidth="md"
      data-testid="create-edit-component-dialog"
    >
      <DialogTitleStyled>
        <Box display="flex" alignItems="center">
          <Box mr={1}>{componentItem ? <Edit size={20} /> : <Plus size={20} />}</Box>
          <PageSubTitle title={title} />
        </Box>
        <Box display="flex" alignItems="center">
          <InfoTooltip
            size={EAvailableSizes.SMALL}
            text="You can add and edit the component name in different languages. This can also be managed later on in the product page communication."
          />
          <StyledSelect
            label="Component name"
            variant="outlined"
            value={selectedLanguage}
            onChange={handleLanguageChange}
            data-testid="component-language-select"
          >
            {productLanguages.map(
              ({ title, key, flag }: { title: string; key: string; flag: string }) => (
                <StyledMenuItem
                  key={`product-lang-${key}`}
                  value={key}
                  data-testid="component-language-select-option"
                >
                  <LanguageSelectorItem
                    value={key}
                    flag={flag}
                    title={title}
                    hoverBackground={false}
                    noPadding={undefined}
                  />
                </StyledMenuItem>
              )
            )}
          </StyledSelect>
        </Box>
      </DialogTitleStyled>
      <TopBackground />
      <StyledDialogContent>
        <Box display="flex" alignItems="center" mb={2}>
          {(!hidePartner || !!infoText) && (
            <InlineHelperText
              variant="INFO"
              helperText={
                !!infoText
                  ? infoText
                  : 'Components are ingredients, packaging, or something else that make up your finished products.'
              }
            />
          )}
        </Box>
        {loading && <Loader overlayOpacity />}
        <CreateEditComponentForm
          formRef={formRef}
          component={componentItem}
          selectableRawMaterialIds={selectableRawMaterialIds}
          company={partner}
          hidePartner={hidePartner}
          language={selectedLanguage}
          onSubmit={handleSubmit}
          onCancel={handleClose}
        />
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default AddEditComponentDialog;
